import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, map, of } from "rxjs";
import { ApiService } from "../core/api.service";

@Component({
  selector: "app-customer-edit-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  template: `
    <section class="dashboard">
      <div class="dashboard-main">
        <header class="dashboard-header">
          <div>
            <h1>{{ customer()?.name || 'Customer' }}</h1>
          </div>
          <div class="header-actions">
            <button class="action-button" (click)="cancel()">Cancel</button>
            <button class="action-button primary" (click)="save()" [disabled]="!form.name.trim() || saving()">
              {{ saving() ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </header>

        @if (!loading()) {
          <div class="panel-card">
            <div class="panel-header">
              <h2>Customer Information</h2>
            </div>
            <div class="panel-content">
              <div class="form-grid">
                <div class="form-field-group">
                  <label>Name</label>
                  <input class="form-input" [(ngModel)]="form.name" />
                </div>
                <div class="form-field-group">
                  <label>Builder</label>
                  <input class="form-input" [(ngModel)]="form.builder" />
                </div>
                <div class="form-field-group">
                  <label>Model</label>
                  <input class="form-input" [(ngModel)]="form.model" />
                </div>
                <div class="form-field-group">
                  <label>Elevation</label>
                  <input class="form-input" [(ngModel)]="form.elevation" />
                </div>
                <div class="form-field-group">
                  <label>Email</label>
                  <input class="form-input" type="email" [(ngModel)]="form.email" />
                </div>
                <div class="form-field-group">
                  <label>Phone</label>
                  <input class="form-input" type="tel" [(ngModel)]="form.phone" />
                </div>
                <div class="form-field-group">
                  <label>Address</label>
                  <input class="form-input" [(ngModel)]="form.address" />
                </div>
              </div>
            </div>
          </div>
        }

        @if (message(); as msg) {
          <div class="status-message">{{ msg }}</div>
        }
      </div>
    </section>
  `,
  styleUrls: ["./customer-create.component.scss"],
})
export class CustomerEditPageComponent {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly customerId: string;

  readonly saving = signal(false);
  readonly message = signal("");

  form = {
    name: "",
    email: "",
    phone: "",
    address: "",
    builder: "",
    model: "",
    elevation: "",
  };

  private readonly customerResource = rxResource({
    params: () => this.customerId,
    stream: ({ params }) =>
      this.api.getCustomer(params).pipe(
        map((customer) => {
          this.form = {
            name: customer.name ?? "",
            email: customer.email ?? "",
            phone: customer.phone ?? "",
            address: customer.address ?? "",
            builder: customer.builder ?? "",
            model: customer.model ?? "",
            elevation: customer.elevation ?? "",
          };
          return customer;
        }),
        catchError(() => {
          this.message.set("Failed to load customer.");
          return of(null);
        }),
      ),
  });
  readonly customer = this.customerResource.value;
  readonly loading = this.customerResource.isLoading;

  constructor() {
    this.customerId = this.route.snapshot.paramMap.get("customerId") || "";
  }

  cancel() {
    this.router.navigate(["/customers", this.customerId]);
  }

  save() {
    const name = this.form.name.trim();
    if (!name) return;

    this.saving.set(true);
    this.api
      .updateCustomer(this.customerId, {
        name,
        email: this.form.email.trim() || undefined,
        phone: this.form.phone.trim() || undefined,
        address: this.form.address.trim() || undefined,
        builder: this.form.builder.trim() || undefined,
        model: this.form.model.trim() || undefined,
        elevation: this.form.elevation.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.router.navigate(["/customers", this.customerId]);
        },
        error: () => {
          this.message.set("Failed to save customer.");
          this.saving.set(false);
        },
      });
  }
}
